import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';

import { BannerQuestion, Container, Image, Section, Skeleton } from 'components';
import {
  AccountNotReadyWarningSection,
  AutoInvestment,
  BalanceStats,
  Portfolio,
} from 'components/pages';
import { createProfileManager, createStatsProvider, createSurveyProvider } from 'di';
import { useAppDispatch, useErrorCallback } from 'hooks';

import { Survey } from '../../../../../api-client';
import download from '../../../../../assets/images/svg/download.svg';
import BannerBlock, {
  BannerBlockVariant,
} from '../../../../../components/pages/my-money/AccountNotReadyWarningSection/BannerBlock';
import apiClientConfig from '../../../../../configs/app';
import { set as setMoneyData } from '../../../../../store/my-money';
import { set as setProfile } from '../../../../../store/profile';
import { Environment } from '../../../../../utils';
import { ymAsync } from '../../../../../utils/functions/ymWrapper';

import './MyMoney.scoped.scss';

const MyMoney = () => {
  const [loading, setLoading] = useState(true);
  const [surveys, setSurveys] = useState<Survey[]>();
  const handleError = useErrorCallback();
  const dispatch = useAppDispatch();

  useEffect(() => {
    const onMount = async () => {
      try {
        await initMyMoneyData();
        const provider = await createSurveyProvider();
        const surveys = await provider.getList();
        setSurveys(surveys);
      } catch (error) {
        if (error instanceof Error) {
          await handleError(error);
        }
      } finally {
        setLoading(false);
      }
    };
    (async () => {
      await onMount();
    })();
  }, []);

  const initMyMoneyData = async () => {
    const statsProvider = await createStatsProvider();
    const myMoneyData = await statsProvider.getStats();
    dispatch(setMoneyData(myMoneyData));
    const manager = await createProfileManager();
    const profile = await manager.get();
    dispatch(setProfile(profile));
    ymAsync('tab-my-money');
  };

  const exportStatistics = async (): Promise<void> => {
    const provider = await createStatsProvider();
    const response = await provider.exportStatistics();
    window.location.assign(apiClientConfig.apiBaseUrl + response.url);
  };

  const devAndTest =
    apiClientConfig.applicationEnvironment === Environment.dev ||
    apiClientConfig.applicationEnvironment === Environment.test;
  return (
    <>
      <Outlet />
      <Container>
        <Section className="my-money-section">
          <AccountNotReadyWarningSection />
          {surveys && surveys?.length > 0 && (
            <Container>
              <BannerQuestion>
                <BannerBlock
                  variant={BannerBlockVariant.SURVEY}
                  params={surveys[0].uuid ? surveys[0].uuid : ''}
                />
              </BannerQuestion>
            </Container>
          )}
          {loading ? (
            <div className="my-money__skeleton" aria-busy="true" aria-live="polite">
              <Skeleton className="my-money__skeleton-item my-money__skeleton-item--portfolio" />
              <Skeleton className="my-money__skeleton-item my-money__skeleton-item--balance" />
              <Skeleton className="my-money__skeleton-item my-money__skeleton-item--rest" />
            </div>
          ) : (
            <div className="my-money__stats">
              {devAndTest && (
                <div onClick={exportStatistics} className="my-money__download">
                  <Image
                    src={download}
                    alt={'download'}
                    className={'primary-market__download__icon'}
                  />
                </div>
              )}
              <Portfolio />
              <div className="my-money__balance">
                <BalanceStats />
              </div>
              <AutoInvestment />
            </div>
          )}
        </Section>
      </Container>
    </>
  );
};

export default MyMoney;
